import { useState, lazy, Suspense } from 'react'
import { ExternalLink, ChevronDown, Clock, Check, Loader2, Code, Copy, X } from 'lucide-react'
import { Button } from '@/components/serge'
import { cn } from '@/lib/utils'
import toast from 'react-hot-toast'

const ExcalidrawViewer = lazy(() => import('./ExcalidrawViewer'))

export interface DeliverableHistory {
  id: number
  version: string
  status: string
  createdAt: string
  metadata?: {
    googleDocs?: {
      documentLink?: string
    }
    diagramCode?: {
      language: string
      code: string
    }
  }
}

interface DeliverableDropdownProps {
  label: string
  history: DeliverableHistory[]
  onGenerate: () => void
  isGenerating: boolean
  disabledReason?: string
  className?: string
}

export default function DeliverableDropdown({
  label,
  history,
  onGenerate,
  isGenerating,
  disabledReason,
  className,
}: DeliverableDropdownProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [codeModalOpen, setCodeModalOpen] = useState(false)
  const [selectedCode, setSelectedCode] = useState<{ language: string; code: string; version: string } | null>(null)

  const hasHistory = history.length > 0
  const latestVersion = history[0]
  const latestLink = latestVersion?.metadata?.googleDocs?.documentLink ?? null
  const generationDisabled = Boolean(disabledReason)

  const handleOpenLink = (link: string) => {
    window.open(link, '_blank', 'noopener,noreferrer')
    setIsOpen(false)
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'generating':
        return <Loader2 className="w-3 h-3 animate-spin text-primary" />
      case 'draft':
      case 'validated':
        return <Check className="w-3 h-3 text-green-500" />
      default:
        return <Clock className="w-3 h-3 text-foreground-muted" />
    }
  }

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr)
    return new Intl.DateTimeFormat('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date)
  }

  return (
    <div className={cn('relative', className)}>
      <div className="flex gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={onGenerate}
          disabled={isGenerating || generationDisabled}
          title={generationDisabled ? disabledReason : undefined}
          className="flex-1"
        >
          {isGenerating ? (
            <>
              <Loader2 className="w-3 h-3 animate-spin mr-2" />
              Génération...
            </>
          ) : generationDisabled ? (
            disabledReason
          ) : (
            'Générer'
          )}
        </Button>

        {hasHistory && history.length === 1 && (
          <button
            type="button"
            onClick={() => latestLink && handleOpenLink(latestLink)}
            className="h-10 px-3 border border-border text-foreground hover:border-primary hover:text-primary transition-colors flex items-center gap-2 disabled:opacity-50"
            disabled={!latestLink || isGenerating}
          >
            <span className="text-sm">v{latestVersion.version}</span>
            <ExternalLink className="w-4 h-4" />
          </button>
        )}

        {hasHistory && history.length > 1 && (
          <button
            type="button"
            onClick={() => setIsOpen(!isOpen)}
            className="h-10 px-3 border border-border text-foreground hover:border-primary hover:text-primary transition-colors flex items-center gap-2"
            disabled={isGenerating}
          >
            <span className="text-sm">v{latestVersion.version}</span>
            <ChevronDown
              className={cn(
                'w-4 h-4 transition-transform',
                isOpen && 'rotate-180'
              )}
            />
          </button>
        )}
      </div>

      {isOpen && hasHistory && (
        <>
          <div
            className="fixed inset-0 z-10"
            onClick={() => setIsOpen(false)}
          />
          <div className="absolute right-0 mt-2 w-80 bg-surface border border-border shadow-lg z-20 max-h-80 overflow-y-auto">
            <div className="p-3 border-b border-border">
              <p className="text-xs font-mono text-foreground-muted uppercase tracking-wider">
                Historique - {label}
              </p>
            </div>
            <div className="divide-y divide-border">
              {history.map((item) => {
                const googleDocs = item.metadata?.googleDocs
                const hasLink = !!googleDocs?.documentLink

                return (
                  <div
                    key={item.id}
                    className="p-3 hover:bg-bg transition-colors"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-mono font-semibold text-foreground">
                            Version {item.version}
                          </span>
                          {getStatusIcon(item.status)}
                        </div>
                        <p className="text-xs text-foreground-muted mt-1">
                          {formatDate(item.createdAt)}
                        </p>
                      </div>
                      {hasLink && (
                        <button
                          type="button"
                          onClick={() => handleOpenLink(googleDocs.documentLink!)}
                          className="flex items-center gap-1 text-xs text-primary hover:underline"
                        >
                          <ExternalLink className="w-3 h-3" />
                          Ouvrir
                        </button>
                      )}
                    </div>
                    {!hasLink && !item.metadata?.diagramCode && (
                      <p className="text-xs text-foreground-muted italic mt-2">
                        Aucun lien disponible
                      </p>
                    )}
                    {item.metadata?.diagramCode && (
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedCode({
                            language: item.metadata?.diagramCode?.language || 'mermaid',
                            code: item.metadata?.diagramCode?.code || '',
                            version: item.version,
                          })
                          setCodeModalOpen(true)
                          setIsOpen(false)
                        }}
                        className="mt-2 flex items-center gap-1 text-xs text-primary hover:underline"
                      >
                        <Code className="w-3 h-3" />
                        Voir le code {item.metadata.diagramCode.language.toUpperCase()}
                      </button>
                    )}
                  </div>
                )
              })}
            </div>
          </div>
        </>
      )}

      {/* Code viewer for latest version (single history) */}
      {hasHistory && history.length === 1 && latestVersion?.metadata?.diagramCode && (
        <button
          type="button"
          onClick={() => {
            setSelectedCode({
              language: latestVersion.metadata?.diagramCode?.language || 'mermaid',
              code: latestVersion.metadata?.diagramCode?.code || '',
              version: latestVersion.version,
            })
            setCodeModalOpen(true)
          }}
          className="mt-2 flex items-center gap-1 text-xs text-primary hover:underline"
        >
          <Code className="w-3 h-3" />
          Voir le code {latestVersion.metadata.diagramCode.language.toUpperCase()}
        </button>
      )}

      {/* Code Modal */}
      {codeModalOpen && selectedCode && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-shade/50">
          <div className="bg-surface border border-border w-full max-w-4xl max-h-[85vh] flex flex-col shadow-xl">
            {selectedCode.language === 'excalidraw' ? (
              <Suspense
                fallback={
                  <div className="flex items-center justify-center h-96">
                    <Loader2 className="w-6 h-6 animate-spin text-primary" />
                  </div>
                }
              >
                <ExcalidrawViewer
                  data={selectedCode.code}
                  title={label}
                  version={selectedCode.version}
                  onClose={() => {
                    setCodeModalOpen(false)
                    setSelectedCode(null)
                  }}
                  className="h-[80vh]"
                />
              </Suspense>
            ) : (
              <>
                <div className="flex items-center justify-between p-4 border-b border-border">
                  <div className="flex items-center gap-3">
                    <Code className="w-5 h-5 text-primary" />
                    <div>
                      <h3 className="text-sm font-semibold text-foreground">
                        Code source {selectedCode.language.toUpperCase()}
                      </h3>
                      <p className="text-xs text-foreground-muted">
                        {label} - Version {selectedCode.version}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        navigator.clipboard.writeText(selectedCode.code)
                        toast.success('Code copié dans le presse-papiers')
                      }}
                      className="flex items-center gap-2 px-3 py-1.5 text-xs border border-border hover:border-primary hover:text-primary transition-colors"
                    >
                      <Copy className="w-3 h-3" />
                      Copier
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setCodeModalOpen(false)
                        setSelectedCode(null)
                      }}
                      className="p-1.5 text-foreground-muted hover:text-foreground transition-colors"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                </div>
                <div className="flex-1 overflow-auto p-4 bg-bg">
                  <pre className="text-xs font-mono text-foreground whitespace-pre-wrap">
                    {selectedCode.code}
                  </pre>
                </div>
                <div className="p-3 border-t border-border bg-surface">
                  <p className="text-xs text-foreground-muted">
                    💡 Copiez ce code pour l'utiliser dans un éditeur{' '}
                    {selectedCode.language === 'mermaid' ? 'Mermaid' : 'D2'} ou le modifier.
                  </p>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
